import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Loader2, X } from "lucide-react";
import { formatSol, LAMPORTS_PER_SOL, WITHDRAW_FEE_LAMPORTS } from "../contracts/solana/config";
import { formatUsd, useSolUsdPrice } from "../hooks/useSolUsdPrice";

interface Props {
  walletAddress: string | null;
  /** Vault withdrawable balance in lamports (from useSolanaMoney). */
  available: bigint | null;
  /** Un-swept native SOL still in the embedded wallet (also withdrawable). */
  walletBalance: bigint | null;
  /** True while a deposit/withdraw tx is in flight — disables inputs. */
  busy: boolean;
  /** Runs the on-chain withdraw tx (Vault + wallet SOL → external wallet, one atomic tx). */
  withdraw: (sol: number, destination: string) => Promise<string>;
  onClose: () => void;
}

// Quick base58 shape check for the destination input (Solana addresses are
// 32-44 base58 chars). CASE-SENSITIVE — never lowercase a base58 address.
// The authoritative check is `new PublicKey(...)` inside buildWithdrawToTx.
const BASE58_ADDRESS_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

type Currency = "SOL" | "USD";

interface Result {
  type: "success" | "error";
  title: string;
  msg: ReactNode;
}

// Hupper double-bracket mark (same paths as the landing Header logo), white
// per the Withdraw card design.
const Mark = () => (
  <svg viewBox="0 0 60 60" fill="none" className="w-[26px] h-[26px] opacity-95" aria-hidden="true">
    <path d="M6 6 L26 18 L26 42 L6 54 Z" stroke="#f5efe6" strokeWidth="5" strokeLinejoin="miter" />
    <path d="M54 6 L34 18 L34 42 L54 54 Z" stroke="#f5efe6" strokeWidth="5" strokeLinejoin="miter" />
  </svg>
);

// Animated ring + mark, drawn via the wd-draw-* stroke-dash classes (index.css).
const ResultIcon = ({ type }: { type: Result["type"] }) =>
  type === "success" ? (
    <svg width="76" height="76" viewBox="0 0 76 76" className="block" aria-hidden="true">
      <circle className="wd-draw-ring" cx="38" cy="38" r="33" stroke="#5fcf80" strokeWidth="3" fill="rgba(95,207,128,0.07)" strokeLinecap="round" />
      <path className="wd-draw-mark" d="M25 39.5l9 9 17-19" stroke="#5fcf80" strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="76" height="76" viewBox="0 0 76 76" className="block" aria-hidden="true">
      <circle className="wd-draw-ring" cx="38" cy="38" r="33" stroke="#ff5a5a" strokeWidth="3" fill="rgba(255,90,90,0.07)" strokeLinecap="round" />
      <path className="wd-draw-mark" d="M28 28l20 20M48 28L28 48" stroke="#ff5a5a" strokeWidth="4" fill="none" strokeLinecap="round" />
    </svg>
  );

/**
 * SolanaWithdrawModal — pays SOL out of the FULL Hupper balance (Vault
 * `available` + un-swept SOL in the embedded wallet) to an EXTERNAL wallet
 * (exchange / personal wallet), NOT the Privy embedded wallet. Wallet SOL is
 * withdrawable directly so cashing out never requires enabling Quick Play
 * (whose sweep is what moves wallet SOL into the Vault).
 *
 * The payout is one atomic tx built by buildWithdrawToTx: program withdraw
 * (Vault → embedded wallet, only for the part the Vault holds) + System
 * transfer (embedded wallet → destination) of the full amount. Funds never
 * rest in the embedded wallet, so the quick-play auto-sweep can't re-deposit
 * them. ADDRESS input + AMOUNT input + Max button (max = available + wallet
 * SOL − fee reserve) + a Withdraw button calling
 * useSolanaMoney().withdraw(sol, destination).
 *
 * Styling follows the "Withdraw Card" design from the Claude Design
 * "Hupper - Pong" handoff: warm gradient card, glowing top hairline,
 * orange pill button with shine sweep, SOL⇄USD toggle on the amount field
 * (live useSolUsdPrice rate; the tx always sends SOL), and an animated
 * full-card success/error result state that auto-dismisses after 3s.
 * Previous version in legacy/.
 */
export default function SolanaWithdrawModal({ walletAddress, available, walletBalance, busy, withdraw, onClose }: Props) {
  const [amount, setAmount] = useState("");
  const [destination, setDestination] = useState("");
  const [cur, setCur] = useState<Currency>("SOL");
  // True when the user clicked "Max". formatSol ROUNDS the display string, so
  // re-parsing it back to lamports can exceed `max` and revert on-chain. When
  // set, handleSubmit sends EXACTLY `max` lamports instead of the rounded input.
  // Cleared on any manual edit.
  const [maxSelected, setMaxSelected] = useState(false);
  // Full-card result state (success/error). While set, the header + form are
  // hidden and the card height is locked so the card doesn't jump.
  const [result, setResult] = useState<Result | null>(null);
  const [closing, setClosing] = useState(false);
  const [cardHeight, setCardHeight] = useState<number | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const timers = useRef<number[]>([]);

  const { price: solPrice } = useSolUsdPrice();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

  // Withdrawable = Vault balance + wallet SOL above the tx-fee reserve (the
  // embedded wallet pays the fee). Max drains the wallet to exactly 0, which
  // the runtime rent rule allows.
  const walletSpendable =
    (walletBalance ?? 0n) > WITHDRAW_FEE_LAMPORTS ? (walletBalance ?? 0n) - WITHDRAW_FEE_LAMPORTS : 0n;
  const max = (available ?? 0n) + walletSpendable;
  const maxSol = Number(max) / LAMPORTS_PER_SOL;

  // Parse the input into a positive SOL number, or null when empty/invalid.
  // In USD mode the typed value is dollars and converts at the live rate.
  const parsedSol = useMemo(() => {
    if (!amount.trim()) return null;
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return null;
    if (cur === "USD") return solPrice != null && solPrice > 0 ? n / solPrice : null;
    return n;
  }, [amount, cur, solPrice]);

  // Compare against `available` in lamports to avoid float drift.
  const parsedLamports = useMemo(() => {
    if (parsedSol === null) return null;
    return BigInt(Math.round(parsedSol * 1_000_000_000));
  }, [parsedSol]);

  // Max is exempt: the displayed max is ROUNDED (formatSol / USD 2dp), so the
  // re-parsed input can exceed `max` — but handleSubmit clamps a Max send to
  // exactly `max` lamports, so it can never actually overdraw.
  const exceedsBalance = !maxSelected && parsedLamports !== null && parsedLamports > max;

  const trimmedDest = destination.trim();
  const destShapeOk = BASE58_ADDRESS_RE.test(trimmedDest);
  // The embedded wallet is exactly what withdraw must NOT pay — the auto-sweep
  // would fold it straight back into the Vault.
  const destIsSelf = walletAddress !== null && trimmedDest === walletAddress;
  const destValid = destShapeOk && !destIsSelf;

  const submitDisabled = parsedSol === null || exceedsBalance || !destValid || busy || !walletAddress;

  // ── Currency toggle — converts the typed value at the live rate. The tx
  // itself always sends SOL; USD is a display convenience.
  const toggleCurrency = () => {
    if (solPrice == null || solPrice <= 0) return;
    const next: Currency = cur === "SOL" ? "USD" : "SOL";
    const v = Number(amount);
    if (amount.trim() && Number.isFinite(v) && v > 0) {
      setAmount(next === "USD" ? (v * solPrice).toFixed(2) : (v / solPrice).toFixed(6));
    }
    setCur(next);
  };

  const maxLabel = cur === "SOL" ? `Max: ${formatSol(max)} SOL` : `Max: ${formatUsd(maxSol * (solPrice ?? 0))}`;

  const fillMax = () => {
    setAmount(cur === "SOL" ? formatSol(max) : (maxSol * (solPrice ?? 0)).toFixed(2));
    setMaxSelected(true);
  };

  // ── Result state: lock the card height, swap the content, auto-dismiss
  // after 3s — success animates the card out and closes the modal; error
  // returns to the form (destination kept) so the user can retry.
  const showResult = (r: Result) => {
    setCardHeight(cardRef.current?.offsetHeight ?? null);
    setResult(r);
    timers.current.push(
      window.setTimeout(() => {
        if (r.type === "success") {
          setClosing(true);
          timers.current.push(window.setTimeout(onClose, 560));
        } else {
          setResult(null);
          setCardHeight(null);
          setAmount("");
          setMaxSelected(false);
        }
      }, 3000),
    );
  };

  const handleSubmit = async () => {
    if (parsedSol === null || parsedLamports === null || !destValid) return;
    // LOSSLESS Max: clamp the lamports actually sent so they can NEVER exceed
    // `max`. When Max was clicked (or the rounded input lands at/over the cap),
    // send exactly `max` lamports → SOL precisely instead of the rounded value.
    const sendLamports = maxSelected || parsedLamports >= max ? max : parsedLamports;
    const sol = Number(sendLamports) / LAMPORTS_PER_SOL;
    const sentText = `${amount} ${cur}`;
    try {
      await withdraw(sol, trimmedDest);
      // The hook auto-refreshes `available`; show the result then close.
      showResult({
        type: "success",
        title: "Withdrawal sent",
        msg: (
          <>
            <b className="text-[#f5efe6] font-semibold">{sentText}</b> is on its way to your wallet.
          </>
        ),
      });
    } catch (e) {
      showResult({
        type: "error",
        title: "Withdrawal failed",
        msg:
          e instanceof Error && e.message
            ? e.message
            : "We couldn't process this withdrawal. Nothing left your balance — try again.",
      });
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        ref={cardRef}
        style={cardHeight != null ? { height: cardHeight } : undefined}
        className={`relative w-full max-w-[440px] rounded-[24px] border border-[#322a22] bg-[linear-gradient(180deg,rgba(22,18,15,0.98),rgba(10,8,6,0.99))] shadow-[0_50px_120px_-40px_rgba(0,0,0,0.85)] overflow-hidden transition-[opacity,transform] duration-[420ms] ease-[cubic-bezier(0.25,0.1,0.25,1)] ${closing ? "opacity-0 scale-[0.96] translate-y-2" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glowing top hairline */}
        <div className="absolute top-0 left-0 right-0 h-[3px] opacity-90 bg-[linear-gradient(90deg,transparent,#ff5722_30%,#ff8a4a_50%,#ff5722_70%,transparent)]" />

        {result ? (
          /* ── Result state (success / error) ── */
          <div className="h-full flex flex-col items-center justify-center text-center p-8 animate-wd-state-in">
            <div
              className="mb-5"
              style={{
                filter:
                  result.type === "success"
                    ? "drop-shadow(0 0 22px rgba(95,207,128,0.35))"
                    : "drop-shadow(0 0 22px rgba(255,90,90,0.35))",
              }}
            >
              <ResultIcon type={result.type} />
            </div>
            <div className="text-[21px] font-bold tracking-[-0.3px] mb-[7px] text-[#f5efe6]">{result.title}</div>
            <div className="text-[13.5px] text-[#8a7f72] leading-[1.55] max-w-[280px]">{result.msg}</div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-[13px] px-[26px] pt-[26px] pb-[22px] border-b border-[#241e18]">
              <Mark />
              <h1 className="flex-1 m-0 text-[23px] font-bold tracking-[-0.4px] text-[#f5efe6]">Withdraw</h1>
              <button
                onClick={onClose}
                aria-label="Close"
                className="shrink-0 w-[38px] h-[38px] rounded-full bg-white/[0.045] hover:bg-white/[0.09] flex items-center justify-center text-[#8a7f72] hover:text-[#f5efe6] transition-all duration-150"
              >
                <X className="w-[15px] h-[15px]" strokeWidth={2.2} />
              </button>
            </div>

            {/* Body */}
            <div className="p-[26px]">
              {/* Destination — an external Solana wallet (exchange / self-custody). */}
              <label
                htmlFor="sol-wd-dest"
                className="block mb-[9px] text-[12px] font-semibold uppercase tracking-[1.4px] text-[#5a5247]"
              >
                To address
              </label>
              <div className="w-full flex items-center gap-3 bg-[#0c0a08] border border-[#241e18] rounded-[14px] px-[18px] py-4 transition-[border-color,box-shadow] duration-150 focus-within:border-[#ff5722] focus-within:shadow-[0_0_0_3px_rgba(255,87,34,0.12)]">
                <input
                  id="sol-wd-dest"
                  placeholder="Your favorite wallet"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  spellCheck={false}
                  autoComplete="off"
                  disabled={busy}
                  className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[15.5px] font-medium text-[#f5efe6] placeholder-[#5a5247] placeholder:font-normal disabled:opacity-50"
                />
              </div>
              {trimmedDest.length > 0 && !destShapeOk && (
                <div className="mt-2 text-[11px] text-red-400 font-semibold">Not a valid Solana address.</div>
              )}
              {destIsSelf && (
                <div className="mt-2 text-[11px] text-red-400 font-semibold">
                  That's your Hupper game wallet — enter an external address.
                </div>
              )}

              {/* Amount */}
              <div className="mt-[22px] mb-[9px] flex items-baseline justify-between text-[12px] font-semibold uppercase tracking-[1.4px] text-[#5a5247]">
                <label htmlFor="sol-wd-amount">Amount</label>
                <button
                  onClick={fillMax}
                  disabled={max === 0n || busy}
                  className="font-mono text-[11px] font-semibold tracking-[0.8px] text-[#ff5722] hover:text-[#ff8a4a] transition-colors duration-150 disabled:opacity-30"
                >
                  {maxLabel}
                </button>
              </div>
              <div className="w-full flex items-center gap-3 bg-[#0c0a08] border border-[#241e18] rounded-[14px] px-[18px] py-4 transition-[border-color,box-shadow] duration-150 focus-within:border-[#ff5722] focus-within:shadow-[0_0_0_3px_rgba(255,87,34,0.12)]">
                <input
                  id="sol-wd-amount"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value.replace(/[^0-9.]/g, ""));
                    setMaxSelected(false);
                  }}
                  disabled={busy}
                  className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[26px] font-bold tracking-[-0.5px] tabular-nums text-[#f5efe6] placeholder-[#5a5247] placeholder:font-normal disabled:opacity-50"
                />
                {/* SOL ⇄ USD toggle — hidden until the live rate is known. */}
                <button
                  onClick={toggleCurrency}
                  disabled={busy || solPrice == null}
                  aria-label="Switch currency"
                  className="group/cur shrink-0 inline-flex items-center gap-[5px] rounded-full border border-[#322a22] bg-white/5 py-[7px] pl-3 pr-[9px] text-[13px] font-bold tracking-[0.4px] text-[#f5efe6] transition-[border-color,background-color] duration-150 enabled:hover:border-[#ff5722] enabled:hover:bg-[rgba(255,87,34,0.08)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {cur}
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="text-[#8a7f72] transition-colors duration-150 group-hover/cur:text-[#ff5722]">
                    <path d="M8 9l4-4 4 4M8 15l4 4 4-4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
              {exceedsBalance && (
                <div className="mt-2 text-[11px] text-red-400 font-semibold">Exceeds Hupper balance.</div>
              )}

              {/* Withdraw — rounded pill with shine sweep */}
              <button
                onClick={handleSubmit}
                disabled={submitDisabled}
                className="group relative overflow-hidden mt-[22px] w-full h-[58px] rounded-full inline-flex items-center justify-center gap-[10px] bg-[linear-gradient(180deg,#ff8a4a_0%,#ff5722_55%,#ea4a15_100%)] text-[#1a0d05] text-[17px] font-bold tracking-[-0.2px] shadow-[0_16px_40px_-14px_rgba(255,87,34,0.65),inset_0_1px_0_rgba(255,255,255,0.28)] transition-[transform,box-shadow] duration-150 enabled:hover:-translate-y-px enabled:hover:shadow-[0_22px_52px_-16px_rgba(255,87,34,0.8),inset_0_1px_0_rgba(255,255,255,0.32)] enabled:active:translate-y-0 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {busy ? "Withdrawing…" : "Withdraw"}
                {!busy && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M5 12h14M13 6l6 6-6 6" stroke="#1a0d05" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                {!submitDisabled && (
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 -translate-x-[110%] bg-[linear-gradient(110deg,transparent_35%,rgba(255,255,255,0.3)_50%,transparent_65%)] transition-transform duration-700 ease-out group-hover:translate-x-[110%]"
                  />
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
